(() => {
  const SECTION_SELECTOR = '.sw-install-help-reviews';
  const TRACK_SELECTOR = '[data-review-track]';
  const PAGINATION_SELECTOR = '[data-review-pagination]';
  const JUDGE_ROOT_SELECTOR = '[data-live-reviews]';
  const JUDGE_PAGINATION_SELECTOR =
    '[data-judge-review-pagination]';

  const initializedSections = new WeakMap();
  const initializedJudgeSections = new WeakMap();

  const getSections = (scope = document) => {
    const sections = [];

    if (
      scope instanceof Element &&
      scope.matches(SECTION_SELECTOR)
    ) {
      sections.push(scope);
    }

    if (scope.querySelectorAll) {
      scope
        .querySelectorAll(SECTION_SELECTOR)
        .forEach((section) => {
          if (!sections.includes(section)) {
            sections.push(section);
          }
        });
    }

    return sections;
  };

  const initializeSection = (section) => {
    if (!section || initializedSections.has(section)) {
      return;
    }

    const track = section.querySelector(TRACK_SELECTOR);
    const pagination = section.querySelector(
      PAGINATION_SELECTOR
    );

    if (!track || !pagination) {
      return;
    }

    let animationFrame = null;
    let resizeObserver = null;
    let mutationObserver = null;
    let paginationCount = 0;

    const getMetrics = () => {
      const firstCard = track.querySelector(
        '.sw-install-help-reviews__review-card'
      );

      const trackStyles =
        window.getComputedStyle(track);

      const gap =
        parseFloat(
          trackStyles.columnGap ||
            trackStyles.gap ||
            '0'
        ) || 0;

      const step = Math.max(
        1,
        (firstCard?.getBoundingClientRect().width ||
          track.clientWidth) + gap
      );

      return {
        step,
        maximumScroll: Math.max(
          0,
          track.scrollWidth - track.clientWidth
        )
      };
    };

    const buildPagination = (
      step,
      maximumScroll
    ) => {
      const isScrollable =
        track.clientWidth > 0 &&
        maximumScroll > 2;

      section.classList.toggle(
        'sw-install-help-reviews--reviews-scrollable',
        isScrollable
      );

      if (!isScrollable) {
        pagination.hidden = true;
        pagination.replaceChildren();
        paginationCount = 0;
        return;
      }

      const count = Math.max(
        1,
        Math.ceil(maximumScroll / step) + 1
      );

      if (count !== paginationCount) {
        pagination.replaceChildren();
        paginationCount = count;

        for (
          let index = 0;
          index < count;
          index += 1
        ) {
          const dot =
            document.createElement('button');

          dot.type = 'button';
          dot.className =
            'sw-install-help-reviews__review-pagination-dot';

          dot.setAttribute(
            'aria-label',
            `Show customer review slide ${index + 1} of ${count}`
          );

          dot.addEventListener(
            'click',
            () => {
              const metrics = getMetrics();

              track.scrollTo({
                left: Math.min(
                  index * metrics.step,
                  metrics.maximumScroll
                ),
                behavior: 'smooth'
              });
            }
          );

          pagination.appendChild(dot);
        }
      }

      pagination.hidden = false;
    };

    const updatePagination = () => {
      animationFrame = null;

      const {
        step,
        maximumScroll
      } = getMetrics();

      buildPagination(step, maximumScroll);

      if (
        pagination.hidden ||
        paginationCount < 1
      ) {
        return;
      }

      const position = Math.min(
        maximumScroll,
        Math.max(0, track.scrollLeft)
      );

      const activeIndex = Math.min(
        paginationCount - 1,
        Math.max(0, Math.round(position / step))
      );

      pagination
        .querySelectorAll(
          '.sw-install-help-reviews__review-pagination-dot'
        )
        .forEach((dot, index) => {
          const isActive = index === activeIndex;

          dot.classList.toggle(
            'is-active',
            isActive
          );

          dot.setAttribute(
            'aria-current',
            isActive ? 'true' : 'false'
          );
        });
    };

    const scheduleUpdate = () => {
      if (animationFrame !== null) {
        return;
      }

      animationFrame =
        window.requestAnimationFrame(
          updatePagination
        );
    };

    const handleImageLoad = () => {
      scheduleUpdate();
    };

    track.addEventListener(
      'scroll',
      scheduleUpdate,
      {
        passive: true
      }
    );

    window.addEventListener(
      'resize',
      scheduleUpdate,
      {
        passive: true
      }
    );

    track
      .querySelectorAll('img')
      .forEach((image) => {
        if (!image.complete) {
          image.addEventListener(
            'load',
            handleImageLoad
          );
        }
      });

    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(
        scheduleUpdate
      );

      resizeObserver.observe(track);

      Array.from(track.children).forEach(
        (card) => {
          resizeObserver.observe(card);
        }
      );
    }

    if ('MutationObserver' in window) {
      mutationObserver = new MutationObserver(
        () => {
          if (resizeObserver) {
            resizeObserver.disconnect();
            resizeObserver.observe(track);

            Array.from(track.children).forEach(
              (card) => {
                resizeObserver.observe(card);
              }
            );
          }

          scheduleUpdate();
        }
      );

      mutationObserver.observe(track, {
        childList: true,
        subtree: true
      });
    }

    initializedSections.set(section, {
      destroy() {
        track.removeEventListener(
          'scroll',
          scheduleUpdate
        );

        window.removeEventListener(
          'resize',
          scheduleUpdate
        );

        track
          .querySelectorAll('img')
          .forEach((image) => {
            image.removeEventListener(
              'load',
              handleImageLoad
            );
          });

        if (resizeObserver) {
          resizeObserver.disconnect();
        }

        if (mutationObserver) {
          mutationObserver.disconnect();
        }

        if (animationFrame !== null) {
          window.cancelAnimationFrame(
            animationFrame
          );
        }

        initializedSections.delete(section);
      }
    });

    updatePagination();

    window.setTimeout(
      scheduleUpdate,
      100
    );

    window.setTimeout(
      scheduleUpdate,
      500
    );
  };

  const initializeJudgeSection = (section) => {
    if (
      !section ||
      initializedJudgeSections.has(section)
    ) {
      return;
    }

    const judgeRoot = section.querySelector(
      JUDGE_ROOT_SELECTOR
    );
    const pagination = section.querySelector(
      JUDGE_PAGINATION_SELECTOR
    );

    if (!judgeRoot || !pagination) {
      return;
    }

    let animationFrame = null;
    let mutationObserver = null;
    let resizeObserver = null;
    let paginationCount = 0;
    const jumpTimers = new Set();

    const getTranslateX = (element) => {
      if (!element) {
        return 0;
      }

      const transform =
        element.style.transform ||
        window.getComputedStyle(element).transform;

      if (!transform || transform === 'none') {
        return 0;
      }

      const translateMatch = transform.match(
        /translate(?:3d|X)?\(\s*(-?[\d.]+)px/i
      );

      if (translateMatch) {
        const translateValue = parseFloat(
          translateMatch[1]
        );

        return Number.isFinite(translateValue)
          ? translateValue
          : 0;
      }

      const values = transform
        .replace(/^matrix(3d)?\(/, '')
        .replace(/\)$/, '')
        .split(',')
        .map((value) => parseFloat(value.trim()));

      if (transform.startsWith('matrix3d(')) {
        return Number.isFinite(values[12])
          ? values[12]
          : 0;
      }

      return Number.isFinite(values[4])
        ? values[4]
        : 0;
    };

    const getMetrics = () => {
      const carousel = judgeRoot.querySelector(
        '.jdgm-carousel'
      );
      const itemWrapper = judgeRoot.querySelector(
        '.jdgm-carousel__item-wrapper'
      );
      const items = Array.from(
        judgeRoot.querySelectorAll(
          '.jdgm-carousel-item'
        )
      );
      const firstItem = items[0];
      const itemWidth =
        firstItem?.getBoundingClientRect().width || 0;
      const carouselWidth =
        carousel?.getBoundingClientRect().width || 0;

      if (
        !carousel ||
        !itemWrapper ||
        items.length < 1 ||
        itemWidth < 1 ||
        carouselWidth < 1
      ) {
        return null;
      }

      const visibleCount = Math.max(
        1,
        Math.min(
          items.length,
          Math.round(carouselWidth / itemWidth)
        )
      );
      const maximumIndex = Math.max(
        0,
        items.length - visibleCount
      );
      const currentIndex = Math.min(
        maximumIndex,
        Math.max(
          0,
          Math.round(
            Math.abs(getTranslateX(itemWrapper)) /
              itemWidth
          )
        )
      );
      const pageStarts = Array.from(
        {
          length: maximumIndex + 1
        },
        (_, index) => index
      );

      return {
        currentIndex,
        itemsCount: items.length,
        maximumIndex,
        pageStarts,
        leftArrow: judgeRoot.querySelector(
          '.jdgm-carousel__left-arrow'
        ),
        rightArrow: judgeRoot.querySelector(
          '.jdgm-carousel__right-arrow'
        )
      };
    };

    const activateControl = (control) => {
      if (!control) {
        return;
      }

      if (typeof control.click === 'function') {
        control.click();
        return;
      }

      control.dispatchEvent(
        new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
          view: window
        })
      );
    };

    const moveToPage = (pageIndex) => {
      const metrics = getMetrics();

      if (!metrics) {
        return;
      }

      const targetIndex =
        metrics.pageStarts[pageIndex];

      if (!Number.isFinite(targetIndex)) {
        return;
      }

      const positionCount =
        metrics.maximumIndex + 1;
      const forwardSteps =
        (targetIndex - metrics.currentIndex +
          positionCount) %
        positionCount;
      const backwardSteps =
        (metrics.currentIndex - targetIndex +
          positionCount) %
        positionCount;
      const useForward =
        forwardSteps <= backwardSteps;
      const steps = useForward
        ? forwardSteps
        : backwardSteps;
      const control = useForward
        ? metrics.rightArrow
        : metrics.leftArrow;

      if (!control || steps < 1) {
        return;
      }

      for (
        let stepIndex = 0;
        stepIndex < steps;
        stepIndex += 1
      ) {
        const timer = window.setTimeout(
          () => {
            jumpTimers.delete(timer);
            activateControl(control);
          },
          stepIndex * 35
        );

        jumpTimers.add(timer);
      }

      const updateTimer = window.setTimeout(
        () => {
          jumpTimers.delete(updateTimer);
          scheduleUpdate();
        },
        steps * 35 + 500
      );

      jumpTimers.add(updateTimer);
    };

    const buildPagination = (pageStarts) => {
      const count = pageStarts.length;

      if (count <= 1) {
        pagination.hidden = true;
        pagination.replaceChildren();
        paginationCount = 0;
        return;
      }

      if (count !== paginationCount) {
        pagination.replaceChildren();
        paginationCount = count;

        for (
          let index = 0;
          index < count;
          index += 1
        ) {
          const dot =
            document.createElement('button');

          dot.type = 'button';
          dot.className =
            'sw-install-help-reviews__review-pagination-dot';

          dot.setAttribute(
            'aria-label',
            `Show customer review slide ${index + 1} of ${count}`
          );

          dot.addEventListener(
            'click',
            () => moveToPage(index)
          );

          pagination.appendChild(dot);
        }
      }

      pagination.hidden = false;
    };

    const updatePagination = () => {
      animationFrame = null;

      const metrics = getMetrics();

      if (!metrics) {
        pagination.hidden = true;
        return;
      }

      buildPagination(metrics.pageStarts);

      if (
        pagination.hidden ||
        paginationCount < 1
      ) {
        return;
      }

      let activeIndex = 0;

      metrics.pageStarts.forEach(
        (pageStart, index) => {
          if (pageStart <= metrics.currentIndex) {
            activeIndex = index;
          }
        }
      );

      pagination
        .querySelectorAll(
          '.sw-install-help-reviews__review-pagination-dot'
        )
        .forEach((dot, index) => {
          const isActive = index === activeIndex;

          dot.classList.toggle(
            'is-active',
            isActive
          );

          dot.setAttribute(
            'aria-current',
            isActive ? 'true' : 'false'
          );
        });
    };

    const scheduleUpdate = () => {
      if (animationFrame !== null) {
        return;
      }

      animationFrame =
        window.requestAnimationFrame(
          updatePagination
        );
    };

    if ('MutationObserver' in window) {
      mutationObserver = new MutationObserver(
        scheduleUpdate
      );

      mutationObserver.observe(judgeRoot, {
        attributes: true,
        attributeFilter: ['class', 'style'],
        childList: true,
        subtree: true
      });
    }

    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(
        scheduleUpdate
      );

      resizeObserver.observe(judgeRoot);
    }

    window.addEventListener(
      'resize',
      scheduleUpdate,
      {
        passive: true
      }
    );

    initializedJudgeSections.set(section, {
      destroy() {
        window.removeEventListener(
          'resize',
          scheduleUpdate
        );

        if (mutationObserver) {
          mutationObserver.disconnect();
        }

        if (resizeObserver) {
          resizeObserver.disconnect();
        }

        if (animationFrame !== null) {
          window.cancelAnimationFrame(
            animationFrame
          );
        }

        jumpTimers.forEach((timer) => {
          window.clearTimeout(timer);
        });

        jumpTimers.clear();
        initializedJudgeSections.delete(section);
      }
    });

    scheduleUpdate();

    window.setTimeout(
      scheduleUpdate,
      100
    );

    window.setTimeout(
      scheduleUpdate,
      600
    );

    window.setTimeout(
      scheduleUpdate,
      2000
    );
  };

  const initializeSections = (
    scope = document
  ) => {
    getSections(scope).forEach((section) => {
      initializeSection(section);
      initializeJudgeSection(section);
    });
  };

  const destroySections = (
    scope = document
  ) => {
    getSections(scope).forEach(
      (section) => {
        const controller =
          initializedSections.get(section);

        if (
          controller &&
          typeof controller.destroy ===
            'function'
        ) {
          controller.destroy();
        }

        const judgeController =
          initializedJudgeSections.get(section);

        if (
          judgeController &&
          typeof judgeController.destroy ===
            'function'
        ) {
          judgeController.destroy();
        }
      }
    );
  };

  const start = () => {
    initializeSections(document);
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      start,
      {
        once: true
      }
    );
  } else {
    start();
  }

  window.addEventListener(
    'load',
    () => {
      initializeSections(document);
    },
    {
      once: true
    }
  );

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initializeSections(event.target);
    }
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      destroySections(event.target);
    }
  );

  document.addEventListener(
    'shopify:section:reorder',
    () => {
      initializeSections(document);
    }
  );
})();
